import { useEffect } from 'react'
import type { PropertyData } from '../types'
import { useSizes } from '../../../features/products/hooks/useMetaData'

export const usePropertyLogic = (
  propertyData: PropertyData,
  selectedSize: string,
  selectedMaterial: string,
  selectedColor: string,
  setCurrentStep: (step: number) => void,
  showPropertiesModal: boolean
) => {
  const { data: sizesData } = useSizes()
  
  const getAvailableSizes = () => sizesData?.data || []
  
  const getAvailableMaterials = () => {
    if (!selectedSize) return []
    const sizeData = propertyData.sizes.find(s => s.sizeId === selectedSize)
    return sizeData?.materials.map(m => m.name) || []
  }
  
  const getAvailableColors = () => {
    if (!selectedSize) return []
    const sizeData = propertyData.sizes.find(s => s.sizeId === selectedSize)
    const materialToUse = selectedMaterial || sizeData?.materials[0]?.name
    if (!materialToUse) return []
    const materialData = sizeData?.materials.find(m => m.name === materialToUse)
    return materialData?.colors.map(c => c.hex) || []
  }

  const getSizeCostPrice = () => {
    if (selectedSize) {
      const sizeData = propertyData.sizes.find(s => s.sizeId === selectedSize)
      return sizeData?.additionalCostPrice || 0
    }
    return 0
  }

  const getSizePrice = () => {
    if (selectedSize) {
      const sizeData = propertyData.sizes.find(s => s.sizeId === selectedSize)
      return sizeData?.price || 0
    }
    return 0
  }

  const getMaterialCostPrice = () => {
    if (selectedSize) {
      const sizeData = propertyData.sizes.find(s => s.sizeId === selectedSize)
      const materialToUse = selectedMaterial || sizeData?.materials[0]?.name
      if (materialToUse) {
        const materialData = sizeData?.materials.find(m => m.name === materialToUse)
        return materialData?.additionalCostPrice || 0
      }
    }
    return 0
  }

  const getMaterialPrice = () => {
    if (selectedSize) {
      const sizeData = propertyData.sizes.find(s => s.sizeId === selectedSize)
      const materialToUse = selectedMaterial || sizeData?.materials[0]?.name
      if (materialToUse) {
        const materialData = sizeData?.materials.find(m => m.name === materialToUse)
        return materialData?.price || 0
      }
    }
    return 0
  }

  const getColorCostPrice = () => {
    if (selectedSize) {
      const sizeData = propertyData.sizes.find(s => s.sizeId === selectedSize)
      const materialToUse = selectedMaterial || sizeData?.materials[0]?.name
      if (materialToUse) {
        const materialData = sizeData?.materials.find(m => m.name === materialToUse)
        const colorToUse = selectedColor || materialData?.colors[0]?.hex
        if (colorToUse) {
          const colorData = materialData?.colors.find(c => c.hex === colorToUse)
          return colorData?.additionalCostPrice || 0
        }
      }
    }
    return 0
  }

  const getColorPrice = () => {
    if (selectedSize) {
      const sizeData = propertyData.sizes.find(s => s.sizeId === selectedSize)
      const materialToUse = selectedMaterial || sizeData?.materials[0]?.name
      if (materialToUse) {
        const materialData = sizeData?.materials.find(m => m.name === materialToUse)
        const colorToUse = selectedColor || materialData?.colors[0]?.hex
        if (colorToUse) {
          const colorData = materialData?.colors.find(c => c.hex === colorToUse)
          return colorData?.price || 0
        }
      }
    }
    return 0
  }

  const hasMaterialPrices = () => {
    if (!selectedSize) return false
    const currentSizeData = propertyData.sizes.find(s => s.sizeId === selectedSize)
    return currentSizeData?.materials.some(material => material.price > 0) || false
  }

  const updateCurrentStep = () => {
    if (!selectedSize) {
      setCurrentStep(0)
      return
    }
    
    const sizePrice = getSizePrice()
    if (sizePrice === 0) {
      setCurrentStep(0)
      return
    }
    
    if (!selectedMaterial) {
      setCurrentStep(1)
      return
    }
    
    const materialPrice = getMaterialPrice()
    if (materialPrice === 0) {
      setCurrentStep(1)
      return
    }
    
    setCurrentStep(2)
  }

  useEffect(() => {
    if (showPropertiesModal) {
      updateCurrentStep()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSize, selectedMaterial, selectedColor, propertyData])

  return {
    getAvailableSizes,
    getAvailableMaterials,
    getAvailableColors,
    getSizeCostPrice,
    getSizePrice,
    getMaterialCostPrice,
    getMaterialPrice,
    getColorCostPrice,
    getColorPrice,
    hasMaterialPrices
  }
}